import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, computed } from '@angular/core';
import { ActionRole, ButtonType } from '@newroom-connect/library/enums';
import { IActionButtonEvent, IListConfiguration, IListItem, IListItemClickedEvent, IListOptions, IListOptionsSort, ListItemType } from '@newroom-connect/library/interfaces';
import { IsEmptyArrayPipe, IsNonEmptyArrayPipe } from '@newroom-connect/library/pipes';

import { ButtonComponent } from '../buttons/button/button.component';
import { LoadingComponent } from '../loading/loading.component';
import { LoadingSpinnerComponent } from '../loading/loading-spinner/loading-spinner.component';

import { EmptyListComponent } from './empty-list/empty-list.component';
import { GridListComponent } from './grid-list/grid-list.component';
import { ListPaginatorComponent } from './list-paginator/list-paginator.component';
import { RowListComponent } from './row-list/row-list.component';
import { RowCardListComponent } from './row-card-list/row-card-list.component';
import { BaseListComponent } from './abstract/base-list.component';

export type ListLayout = 'grid' | 'rows' | 'row-cards';
export type PaginationType = 'normal' | 'infinite-scroll';

@Component({
  standalone: true,
  imports: [
    ButtonComponent,
    EmptyListComponent,
    GridListComponent,
    ListPaginatorComponent,
    LoadingComponent,
    LoadingSpinnerComponent,
    RowListComponent,
    RowCardListComponent,
    IsEmptyArrayPipe,
    IsNonEmptyArrayPipe
  ],
  selector: 'nrc-list',
  templateUrl: './list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListComponent extends BaseListComponent {
  @Input({ required: true }) public config!: IListConfiguration;
  @Input() public layout?: ListLayout;
  @Input() public listPaginatorOverflowParentElement?: HTMLElement;
  @Input() public hidePaginator?: boolean | null;
  @Input() public paginationType: PaginationType = 'normal';

  @Output() public listItemClickedEvent = new EventEmitter<IListItemClickedEvent>();
  @Output() public listOptionsEvent = new EventEmitter<IListOptions>();
  @Output() public actionButtonEvent = new EventEmitter<IActionButtonEvent>();
  @Output() public configButtonClickEvent = new EventEmitter<{ index: number, key: string }>();
  @Output() public itemActionButtonEvent = new EventEmitter<IActionButtonEvent>();
  @Output() public emptyListActionEvent = new EventEmitter();
  @Output() public rowCollapsedEvent = new EventEmitter<IListItem>();

  public listItemType = ListItemType;
  public actionRole = ActionRole;
  public buttonType = ButtonType;

  public lastPaginationEvent = { page: 1, pageSize: 10 };
  public lastSortEvent?: IListOptionsSort;

  public itemsSelectedText = computed(() => {
    const items = this.items();

    if (!items) {
      return '0 selected';
    }

    return `${items.filter(item => item.isSelected).length} selected`;
  });

  public showPaginator = computed(() => {
    return this.paginationType === 'normal' && !this.hidePaginator;
  });

  /**
   * Emit a list options event based on the stored last events.
   */
  public emitListOptionsEvent(): void {
    this.listOptionsEvent.emit({
      pagination: this.lastPaginationEvent,
      sort: this.lastSortEvent
    });
  }
}
